/**
 * buildGatedRoutes — turns feature-tagged route descriptors into a
 * react-router `RouteObject[]`, OMITTING any route whose release toggle is
 * disabled (not merely nav-hiding it) and code-splitting every surviving
 * route's component via `React.lazy` + `<Suspense>`.
 *
 * A disabled route is absent from the returned array entirely, so a direct URL
 * to it falls through to the SPA's catch-all `<NotFoundPage>`
 * (SPEC-FEATURE-TOGGLES-RULE-005, SCENARIO-003). Because each component loader
 * is `React.lazy`, the disabled feature's chunk is not in the initial bundle.
 *
 * Used by all three per-SPA migration tasks (TASK-FT-MIGRATE-*); this task only
 * provides the helper.
 *
 * @module feature-gate/buildGatedRoutes
 * @see SPEC-FEATURE-TOGGLES
 */

import React, { Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import { getEnabledSet } from "./releaseFeatures";
import { GateLoading } from "./GateLoader";

/** A lazy component loader, as passed to `React.lazy`. */
export type LazyLoader = () => Promise<{ default: React.ComponentType<unknown> }>;

/**
 * One gated route descriptor. `feature` is the canonical dot-key; when omitted
 * the route is permanently on (use sparingly — the CI gate requires either a
 * key or an explicit allowance comment for user-facing routes).
 */
export interface GatedRouteDescriptor {
  /** URL path segment (as in react-router `RouteObject.path`). */
  path?: string;
  /** Marks the index route. */
  index?: boolean;
  /** Canonical release-toggle key; if set, the route is omitted when OFF. */
  feature?: string;
  /** Lazy loader for the route component (code-split out when disabled). */
  loader: LazyLoader;
  /** Optional nested gated children. */
  children?: GatedRouteDescriptor[];
  /** Fallback element shown while the lazy component loads. */
  suspenseFallback?: React.ReactNode;
}

/**
 * Cache of `React.lazy` components keyed by loader identity.
 *
 * `buildGatedRoutes` is called at RENDER time (inside `<ReleaseFeaturesGate>` /
 * `useRoutes`, e.g. `AdminRoutes`), so `lazyElement` runs on every render of the
 * routing component. Calling `React.lazy(loader)` fresh each render mints a NEW
 * component identity every time — React then treats it as a different component,
 * unmounts the mounted route and re-suspends, so navigation to a code-split
 * route freezes on its loading fallback forever (the SPA-navigation freeze).
 *
 * The fix is to return a STABLE `LazyExoticComponent` for a given loader across
 * renders. Route `loader`s live on module-scope `descriptors` consts (see the
 * per-SPA `routes.tsx` under `apps/`), so their function identity is stable —
 * keying the cache
 * by the loader object is correct and lets React reuse the same lazy component.
 * A `WeakMap` lets a loader that is dropped (e.g. a per-call throwaway loader in
 * `gateRoutes`, whose element is discarded) be garbage-collected. Mirrors the
 * "stable identities across renders" pattern in `plugins/registry.ts`.
 *
 * PROVENANCE — how this presents, and why it is easy to misdiagnose. Found on the
 * SUSE gov box, 2026-07-02. Under `v7_startTransition` the symptom is NOT an
 * obvious hang: the URL changes and the NavLink active state advances, but the
 * routed view stays on the PREVIOUS page until a full reload — because the
 * transition suspends, retries, and each retry mints yet another fresh lazy
 * component, so it never commits. It reads as a routing or a permissions bug and
 * is neither. It affects EVERY `buildGatedRoutes` consumer (admin / tenant /
 * workspace), so a fix verified in one SPA is not evidence for the others.
 */
const lazyComponentCache = new WeakMap<
  LazyLoader,
  React.LazyExoticComponent<React.ComponentType<unknown>>
>();

/** Return a render-stable `React.lazy` component for a loader (cached by identity). */
function getLazyComponent(
  loader: LazyLoader,
): React.LazyExoticComponent<React.ComponentType<unknown>> {
  let component = lazyComponentCache.get(loader);
  if (component === undefined) {
    component = React.lazy(loader);
    lazyComponentCache.set(loader, component);
  }
  return component;
}

/**
 * Wrap a lazy component in `<Suspense>` so the disabled-feature chunk stays out
 * of the initial bundle and a loading fallback shows while it streams in. The
 * `React.lazy` component is cached by loader identity (see `getLazyComponent`)
 * so it is stable across the render-time rebuilds of the gated route tree.
 */
function lazyElement(
  loader: LazyLoader,
  fallback: React.ReactNode,
): React.ReactElement {
  const LazyComponent = getLazyComponent(loader);
  return (
    <Suspense fallback={fallback}>
      <LazyComponent />
    </Suspense>
  );
}

/**
 * Build the `RouteObject[]` for a list of gated descriptors against the
 * server-resolved enabled set. A descriptor with a `feature` key that is not in
 * the enabled set is omitted; one without a key (or whose key is enabled) is
 * kept. Filtering recurses into `children`.
 *
 * @param descriptors The feature-tagged route descriptors.
 * @param enabled     The resolved enabled set. Defaults to the live store set
 *                    (`getEnabledSet()`), which is empty/fail-closed until the
 *                    bootstrap resolves — so callers MUST build routes only
 *                    after `bootstrapReleaseFeatures()` (e.g. inside
 *                    `<ReleaseFeaturesGate>`).
 */
export function buildGatedRoutes(
  descriptors: GatedRouteDescriptor[],
  enabled: ReadonlySet<string> = getEnabledSet(),
): RouteObject[] {
  const routes: RouteObject[] = [];

  for (const d of descriptors) {
    // Fail-closed omission: a keyed route survives ONLY if its key is enabled.
    if (d.feature !== undefined && !enabled.has(d.feature)) {
      continue;
    }

    const fallback = d.suspenseFallback ?? <GateLoading />;
    const element = lazyElement(d.loader, fallback);

    if (d.index) {
      // Index routes cannot carry a path or children.
      routes.push({ index: true, element });
      continue;
    }

    const childRoutes =
      d.children && d.children.length > 0
        ? buildGatedRoutes(d.children, enabled)
        : undefined;

    routes.push({
      path: d.path,
      element,
      ...(childRoutes && childRoutes.length > 0 ? { children: childRoutes } : {}),
    });
  }

  return routes;
}
